import { AfterContentChecked, Component, inject, input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ColumnMode, DatatableComponent, SortDirection } from '@siemens/ngx-datatable';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { catchError, EMPTY, finalize, merge } from 'rxjs';
import { NoticeModuleName, NoticeRetrievalMethod } from '../../../../core/components/bx-notices/notices.enum';
import { NoticeModel } from '../../../../core/components/bx-notices/notices.model';
import { Page } from '../../../../core/components/table-footer-paging/table-footer-paging.component';
import { ColumnWidthsService } from '../../../../core/services/column-widths.service';
import { DialogService } from '../../../../core/services/dialog.service';
import { EstimateEventsService } from '../../../../core/services/events/estimate-events.service';
import { JobEventsService } from '../../../../core/services/events/job-events.service';
import { LocalStorageService } from '../../../../core/services/local-storage.service';
import { FeatureService, PermissionService } from '../../../../core/services/permission';
import { StoresService } from '../../../../core/services/stores';
import { TablesHelperService } from '../../../../core/services/tables-helper.service';
import { Customer, ExportFormat, ExportJobsOptions, Tables } from '../../../../data/models';
import { RepoEstimatesService } from '../../../../data/repositories/repo-estimates.service';
import { RepoExportService } from '../../../../data/repositories/repo-export.service';
import { RepoJobsService } from '../../../../data/repositories/repo-jobs.service';
import { RepoSettingsService } from '../../../../data/repositories/repo-settings.service';
import { SharedDataService } from '../../../../data/services/shared-data.service';
import { ReportService } from '../../../../reports/report.service';
import { TableConfig } from '../../../estimates';
import { EstimatesService } from '../../../estimates/estimates.service';
import { LayoutService } from '../../../layout/layout.service';
import { MessagesService } from '../../../messages/messages.service';
import { CreateJobOptions, JobsService } from '../../jobs.service';
import { JobsListApiService } from './domain/apis/jobs-list-api.service';
import { JobCopyOptionsInput, JobFilterInput, JobSortInput } from '../../../../../graphql';
import { SortEvent } from '../../../../data/models/datatable.model';
import { JobListModel } from './domain/entities/job-list.model';
import { first } from 'rxjs/operators';
import { BreezeCase } from '../../../../utils/types/convert-case';

type TableColWidths = {
    Number: number;
    Description: number;
    BuildingType: number;
    ProgressPercent: number;
    VariancePercent: number;
    InvoicedPercent: number;
    GrossProfit: number;
    ClientName: number;
    CreationDate: number;
} & Record<string | number, number>;

type SortFields = {
    [key: string]: JobSortInput;
};

@UntilDestroy()
@Component({
    selector: 'app-jobs-list2',
    templateUrl: './jobs-list2.component.html',
    standalone: false,
})
export class JobsList2Component implements OnInit, AfterContentChecked {
    #jobsListApiService = inject(JobsListApiService);

    client = input<BreezeCase<Customer> | undefined>();
    rows: JobListModel[] = [];
    page = new Page();
    search?: string;
    sortField?: string = 'createdAt';
    sortDirection: 'ASC' | 'DESC' = 'DESC';
    // @ts-ignore TS2564
    modalRef: BsModalRef;
    title = 'Jobs';
    isLoading = true;
    // @ts-ignore TS7008
    currentComponentWidth;
    status: string | undefined;
    deletedView = false;
    tableColumnMode: 'flex' | 'force' = 'flex';
    jobsWidths = {} as TableColWidths;
    tableHeight = 'auto';
    modulePermitted = true;
    jobIds: string[] = [];

    settingsActions = [
        {
            groupName: 'Display Only',
            actions: [
                { label: 'Active Items', condition: true, value: 0 },
                { label: 'Deleted Items', condition: true, value: 1 },
            ],
        },
    ];

    simpleItems = [
        {
            actions: [{ label: 'Reset View to Default', condition: true, value: 0 }],
        },
    ];

    moreActions = [
        {
            value: 0,
            label: 'Print Job WIP Report',
            condition: true,
        },
        {
            value: 1,
            label: 'Email Job WIP Report',
            condition: true,
        },
        {
            value: 2,
            label: 'Export to Excel',
            condition:
                this.permissionService.isAvailable('jobs.maintain-job-costings.view-estimated') &&
                this.permissionService.isAvailable('jobs.maintain-job-costings.view-actual') &&
                this.permissionService.isAvailable('jobs.maintain-job-costings.view-variance'),
        },
    ];
    // @ts-ignore TS7008
    @ViewChild('tableWrapper') tableWrapper;
    // @ts-ignore TS2564
    @ViewChild('jobsTable') table: DatatableComponent;

    noticeModuleNames = [NoticeModuleName.Jobs, NoticeModuleName.Variations];
    // @ts-ignore TS2564
    noticeRetrievalMethod: NoticeRetrievalMethod = NoticeRetrievalMethod.Module;

    estimateCreationViaLeadOnly = false;

    constructor(
        public layoutService: LayoutService,
        private jobsService: JobsService,
        private reportService: ReportService,
        private messagesService: MessagesService,
        private settings: RepoSettingsService,
        private jobs: RepoJobsService,
        public permissionService: PermissionService,
        public featureService: FeatureService,
        private estimates: RepoEstimatesService,
        private localStorageService: LocalStorageService,
        private columnWidthsService: ColumnWidthsService,
        private repoExportService: RepoExportService,
        private estimatesService: EstimatesService,
        private tablesHelperService: TablesHelperService,
        private readonly storesService: StoresService,
        private dialogService: DialogService,
        private estimateEventsService: EstimateEventsService,
        private jobEventsService: JobEventsService,
        private sharedData: SharedDataService,
        private readonly route: ActivatedRoute,
        private readonly router: Router
    ) {
        setTimeout(() => {
            this.modulePermitted =
                this.permissionService.isAvailable('jobs.jobs-access.jobs-menu') && this.featureService.isStateAvailable('jobs');
        }, 500);

        this.storesService.hasUserEnteredApp$.pipe(untilDestroyed(this)).subscribe(() => {
            this.estimateCreationViaLeadOnly = !!this.sharedData.UserData?.franchise?.EstimateCreationViaLeadOnly;
        });
    }

    ngOnInit() {
        if (this.localStorageService.getObject('jobs-table')) {
            this.tableColumnMode = 'force';
            this.jobsWidths = this.localStorageService.getObject('jobs-table');
        }

        this.page = new Page();

        this.route.queryParams.pipe(untilDestroyed(this)).subscribe(() => {
            this.resetPageNumber();
            this.noticeRetrievalMethod = this.client() ? NoticeRetrievalMethod.ObjectIds : NoticeRetrievalMethod.Module;
            this.load();
            this.setDefaultConfig();
            this.getJobsList();
        });

        merge(this.jobEventsService.jobAdded$, this.jobEventsService.jobDeleted$, this.jobEventsService.jobRecovered$)
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.resetPageNumber();
                this.getJobsList();
            });

        if (this.layoutService.isMobile()) {
            this.tableColumnMode = 'force';
        } else if (!this.localStorageService.getObject('jobs-table')) {
            this.tableColumnMode = 'flex';
        }
    }

    ngAfterContentChecked() {
        if (this.table && this.tableWrapper.nativeElement.clientWidth !== this.currentComponentWidth) {
            this.currentComponentWidth = this.tableWrapper.nativeElement.clientWidth;
            this.rows = [...this.rows];
        }
    }

    load() {
        this.isLoading = true;
        this.status = this.route.snapshot.queryParams.status;
        // reset the sidepanel
        this.storesService.updateSubpanelFilterStatus({ submenu: 'jobs', enabled: true });

        if (this.status === 'notstarted') {
            this.title = 'Jobs Not Started';
        }
        if (this.status === 'inprogress') {
            this.title = 'Jobs In Progress';
        }
        if (this.status === 'completed') {
            this.title = 'Jobs Completed';
        }
    }

    // @ts-ignore TS7006
    pageLimit(event) {
        this.page.size = Number.parseInt(event.target.textContent);
        this.page.pageNumber = 0;
        this.settings.JobsListViewPageCount = this.page.size;
        this.getJobsList();
    }

    setPage(pageInfo: { offset: number }) {
        if (this.page.pageNumber !== pageInfo.offset) {
            this.page.pageNumber = pageInfo.offset;
            this.getJobsList();
        }
    }

    setSearch(event: string | undefined) {
        this.isLoading = true;
        this.search = event;
        this.page.pageNumber = 0;
        this.getJobsList();
    }

    setDefaultConfig() {
        this.page.pageNumber = 0;
        this.page.size = this.settings.JobsListViewPageCount;
    }

    onSort(event: SortEvent<any>) {
        if (event.sorts.length) {
            const newSortField = event.sorts[0].prop.toString();
            const oldSortField = this.sortField;
            this.sortField = event.sorts[0].prop.toString();
            // For datatable, when switching to a different column for sorting,
            // it always defaults to DESC
            if (newSortField !== oldSortField) {
                this.sortDirection = 'DESC';
            } else {
                this.sortDirection = event.sorts[0].dir.toUpperCase() as 'ASC' | 'DESC';
            }
            this.getJobsList();
        }
    }

    getJobsList() {
        const take = this.page.size;
        const skip = this.page.pageNumber * this.page.size;
        let sort: JobSortInput = {};
        if (this.sortField) {
            // Ensure that the sortfield exists as a property of JobSortInput
            sort = { [this.sortField]: this.sortDirection };
        }

        let where: JobFilterInput = { and: [] };
        where.and!.push({ isDeleted: { eq: this.deletedView } });

        const client = this.client();
        if (client) {
            where.and!.push({ iD_Customer: { eq: client.ID_Customer } });
        }

        this.#jobsListApiService
            .getJobs(take, skip, this.search, this.status, [sort], where)
            .pipe(
                first(),
                catchError((err) => {
                    return EMPTY;
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((result) => {
                if (result.data.jobs) {
                    this.page.totalElements = result.data.jobs.totalCount;
                    this.rows = [...result.data.jobs.items!];
                    if (this.noticeRetrievalMethod == NoticeRetrievalMethod.ObjectIds) {
                        this.jobIds = this.rows.map((j) => j.iD_Job);
                    }
                    setTimeout(() => {
                        this.table.columnMode = ColumnMode.force;
                    }, 2000);
                }
            });
    }

    addJob() {
        let options: CreateJobOptions = {};
        if (this.client()) {
            options.Name = this.client()?.Name;
        }
        this.jobsService.newJob(options).catch(() => ({}));
    }

    copyJob(job: JobListModel) {
        let options: any = {};
        if (this.client()) {
            options.name = this.client()?.Name;
        }
        this.jobsService.copyJob(job, options).catch(() => ({}));
    }

    createEstimateFromJob(job: JobListModel) {
        const options: any = {
            IsPCPSRedistDefault: true,
            Template: false,
            Description: job.description,
            Name: job.clientName,
            Address: job.clientAddress1,
            Suburb: job.clientCityTown,
            State: job.clientState,
            PostCode: job.clientPostCode,
            Phone: job.clientPhone,
            Mobile: job.clientMobile,
            Email: job.clientEmail,
            CopyToWork: false,
            CalculationMethod: 1,
            WorksLocationAddress: job.worksLocationAddress,
            WorksLocationCityTown: job.worksLocationSuburb,
            WorksLocationState: job.worksLocationState,
            WorksLocationPostcode: job.worksLocationPostcode,
            CopyCategoriesFromJobId: job.iD_Job,
        };
        this.estimatesService.newEstimate(options).catch(() => ({}));
    }

    async deleteJob(evt: MouseEvent, job: JobListModel) {
        evt.stopPropagation();
        await this.jobsService.deleteJob(job);
    }

    printJobWIP() {
        this.reportService
            .getDateRange({
                title: 'Job WIP',
                text: 'Select a date range for this report:',
            })
            // @ts-ignore TS7006
            .then((res) => {
                this._printJobWIP(res);
            })
            .catch(() => ({}));
    }

    // @ts-ignore TS7006
    _printJobWIP(options) {
        const fileName = 'Job WIP Report';
        const params = {
            reportName: 'jobwip',
            reportTitle: 'Job WIP Report',
            params: {
                DateFrom: options.from,
                DateTo: options.to,
            },
            fileName: fileName,
        };
        this.reportService.generate(params);
    }

    emailJobWIP() {
        this.reportService
            .getDateRange({
                title: 'Job WIP',
                text: 'Select a date range for this report:',
            })
            // @ts-ignore TS7006
            .then((res) => {
                this._emailJobWIP(res);
            })
            .catch(() => ({}));
    }

    // @ts-ignore TS7006
    _emailJobWIP(options) {
        const fileName = 'Job WIP Report';
        const params = {
            reportName: 'jobwip',
            reportTitle: 'Job WIP Report',
            params: {
                DateFrom: options.from,
                DateTo: options.to,
            },
            format: 'pdf',
            fileName: fileName,
        };
        // @ts-ignore TS7006
        this.reportService.generate(params).then((response) => {
            this.messagesService.sendEmail({
                Subject: 'Job WIP Report',
                StorageAttachments: [response.data],
            });
        });
    }

    // Recycle Bin
    // @ts-ignore TS7006
    resolveSettingsAction(value) {
        this.isLoading = true;
        switch (value) {
            case 0: {
                // active items
                this.setActiveView();
                break;
            }
            case 1: {
                // deleted items
                this.setDeletedView();
                break;
            }
        }
    }

    // @ts-ignore TS7006
    selectedSimpleItem(val) {
        this.resolveSimpleItemsSettingsAction(val);
    }

    // @ts-ignore TS7006
    resolveSimpleItemsSettingsAction(value) {
        switch (value) {
            case 0: {
                this.isLoading = true;
                this.jobsWidths = {} as TableColWidths;
                this.resetColumnWidths();
                const tempRows = [...this.rows];
                this.rows.length = 0;

                setTimeout(() => {
                    this.rows = [...tempRows];
                    this.isLoading = false;
                }, 100);
                break;
            }
        }
    }

    resetColumnWidths() {
        this.columnWidthsService.resetTableUsingTableName(Tables.Jobs);
    }

    exportToExcel() {
        const options: ExportJobsOptions = {
            format: ExportFormat.XLSX,
            deleted: this.deletedView,
            filter: this.search,
            status: this.status ? this.status.split('|') : undefined,
        };
        this.repoExportService.exportJobs(options);
    }

    // @ts-ignore TS7006
    resolveAction(action) {
        switch (action) {
            case 0: {
                this.printJobWIP();
                break;
            }
            case 1: {
                this.emailJobWIP();
                break;
            }
            case 2: {
                this.exportToExcel();
                break;
            }
        }
    }

    setDeletedView() {
        this.title = `Deleted ${this.title}`;
        this.deletedView = true;
        this.setDefaultConfig();
        this.storesService.updateSubpanelFilterStatus({ submenu: 'jobs', enabled: false });
        this.getJobsList();
    }

    setActiveView() {
        this.deletedView = false;
        this.title = 'Jobs';
        this.setDefaultConfig();
        this.storesService.updateSubpanelFilterStatus({ submenu: 'jobs', enabled: true });
        this.getJobsList();
    }

    async recoveryJobs(job: JobListModel) {
        await this.jobsService.recoverJob(job);
    }

    onResize() {
        const jobsColWidths = {} as TableColWidths;
        this.table._internalColumns.forEach((row) => {
            const colKey = row.prop;
            const colWidth = row.width;
            if (colKey && colWidth) jobsColWidths[colKey] = colWidth;
        });
        this.localStorageService.saveObject('jobs-table', jobsColWidths);
    }

    private resetPageNumber() {
        if (this.tablesHelperService.isResetPageNumber(this.page.totalElements - 1, this.page.size, this.page.pageNumber))
            this.page.pageNumber--;
    }

    onViewNotice(notice: NoticeModel) {
        if (notice.module === NoticeModuleName.Jobs) {
            this.router.navigate(['/jobs', 'list', notice.objectId, 'selections']);
        } else {
            this.router.navigate(['/jobs', 'list', notice.objectId, 'variations']);
        }
    }
}
